function sectionIdOf(el: Element | null): string | null {
  if (!el) return null;
  const withData = el.closest("[data-section-id]");
  if (withData instanceof HTMLElement && withData.dataset.sectionId)
    return withData.dataset.sectionId;
  const withId = el.closest('[id^="shopify-section-"]');
  if (withId && withId.id.startsWith("shopify-section-"))
    return withId.id.slice("shopify-section-".length);
  return null;
}

function collectSectionRoots(selectors: string[]): Map<string, Element> {
  const sectionRoots = new Map<string, Element>();
  for (const selector of selectors) {
    document.querySelectorAll(selector).forEach((node) => {
      const sectionId = sectionIdOf(node);
      if (!sectionId) return;
      const sectionRoot =
        document.querySelector(`[data-section-id="${sectionId}"]`) ||
        document.getElementById(`shopify-section-${sectionId}`);
      if (sectionRoot) sectionRoots.set(sectionId, sectionRoot);
    });
  }
  return sectionRoots;
}

function replaceFromDoc(
  targetRoot: ParentNode,
  sourceDoc: ParentNode,
  selectors: string[]
) {
  const updatedSelectors = new Set<string>();
  for (const selector of selectors) {
    const currentNodes = targetRoot.querySelectorAll(selector);
    if (!currentNodes.length) continue;
    const sourceNodes = sourceDoc.querySelectorAll(selector);
    const count = Math.min(currentNodes.length, sourceNodes.length);
    let changed = false;
    for (let i = 0; i < count; i++) {
      const freshNode = sourceNodes[i];
      if (!(freshNode instanceof Element)) continue;
      if (currentNodes[i].outerHTML === freshNode.outerHTML) continue;
      currentNodes[i].replaceWith(document.importNode(freshNode, true));
      changed = true;
    }
    if (changed) updatedSelectors.add(selector);
  }
  updatedSelectors.forEach((selector) => {
    document.dispatchEvent(
      new CustomEvent("shopify:section:load", { detail: { selector } })
    );
  });
}

export async function updateElements(
  selectors: string[],
  sectionId?: string
): Promise<void> {
  if (!selectors.length) return;
  const hasAnyTarget = selectors.some(
    (selector) => !!document.querySelector(selector)
  );
  if (!hasAnyTarget) return;

  const sectionRoots = collectSectionRoots(selectors);
  try {
    if (sectionId || sectionRoots.size > 0) {
      const sectionIds = sectionId
        ? [sectionId]
        : Array.from(sectionRoots.keys());
      const response = await fetch(`?sections=${sectionIds.join(",")}`);
      if (!response.ok) return;
      const sectionsPayload: unknown = await response.json();
      const sectionDocuments = new Map<string, Document>();
      if (sectionsPayload && typeof sectionsPayload === "object") {
        for (const [sectionKey, sectionHtml] of Object.entries(
          sectionsPayload
        )) {
          if (typeof sectionHtml === "string")
            sectionDocuments.set(
              sectionKey,
              new DOMParser().parseFromString(sectionHtml, "text/html")
            );
        }
      }
      for (const [secId, sectionRoot] of sectionRoots.entries()) {
        const sectionDoc = sectionDocuments.get(secId);
        if (sectionDoc) replaceFromDoc(sectionRoot, sectionDoc, selectors);
      }
      return;
    }

    const responseFull = await fetch(location.href);
    if (!responseFull.ok) return;
    const html = await responseFull.text();
    const fullDoc = new DOMParser().parseFromString(html, "text/html");
    replaceFromDoc(document, fullDoc, selectors);
  } catch (err) {
    console.warn("Update Elements failed:", err);
  }
}
